import React from 'react';
import CncImg from './assets/CncImg.png';
import PrintingImg from './assets/PrintingImg.png';
import MoldingImg from './assets/MoldingImg.png';
import SheetMetalImg from './assets/SheetMetalImg.png';
import CastingImg from './assets/CastingImg.png';
import DesignImg from './assets/DesignImg.png';
import {
  ServiceType_Pcb_192,
  SuggestionCardIcon,
  SuggestionCardIconHover
} from '@assets';
import { CustomizedTypography } from '@components/Customized';
import { colorSet } from '@components/Provider';
import {
  Grid,
  Box,
  useTheme,
  useMediaQuery,
  makeStyles
} from '@material-ui/core';
import { Help, ArrowForwardOutlined } from '@material-ui/icons';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginBottom: 84,
      '& .title-description': {
        cursor: 'pointer',
        marginBottom: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiSvgIcon-root': {
          marginRight: 4,
          color: colorSet.gray700
        }
      },
      [theme.breakpoints.down('xs')]: {
        marginBottom: 100,
        '& .title-description': {
          marginBottom: 36
        }
      }
    },
    gridItem: {
      '& .servicetype-help': {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        '& .MuiSvgIcon-root': {
          marginLeft: 4,
          color: colorSet.gray700
        }
      }
    },
    serviceTypeCard: {
      marginBottom: 12,
      borderRadius: 4,
      border: `1px solid ${colorSet.gray400}`,
      cursor: 'pointer',
      '& img': {
        width: '100%',
        height: 240
      },
      '& .suggestion-img': {
        height: 55,
        width: 55
      },
      '& .servicetype-text': {
        padding: 12
      }
    },
    serviceTypeCardMobile: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: 8,
      cursor: 'pointer',
      padding: '16px 20px',
      backgroundColor: colorSet.gray100,
      '& img': {
        borderRadius: 8,
        width: 56,
        height: 56
      }
    }
  };
});

interface Props {
  onClick: () => void;
}

const ServiceTypeCardPresenter: React.FC<Props> = ({ onClick }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();

  const ServiceTypeCard = ({
    servicetype,
    description,
    img,
    onClick
  }: {
    servicetype: string;
    description?: string;
    img: string;
    onClick: () => void;
  }) => {
    if (matches) {
      return (
        <Grid className={classes.gridItem} item xs={12} sm={4}>
          <Box className={classes.serviceTypeCardMobile} onClick={onClick}>
            <div className="servicetype-text">
              <CustomizedTypography
                variant="body1"
                fontWeight="bold"
                gutterBottoms={description ? 12 : 0}
              >
                {servicetype}
              </CustomizedTypography>
              <CustomizedTypography
                variant="body2"
                fontWeight="500"
                color="textSecondary"
              >
                {description}
              </CustomizedTypography>
            </div>
            <img src={img} alt="cnc" />
          </Box>
        </Grid>
      );
    } else {
      return (
        <Grid className={classes.gridItem} item xs={12} sm={4}>
          <Box className={classes.serviceTypeCard} onClick={onClick}>
            {servicetype === '서비스 개설 제안' ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gridGap={16}
                height={240}
                bgcolor={colorSet.gray200}
              >
                <Box
                  borderRadius="50%"
                  width={54}
                  height={54}
                  bgcolor={colorSet.gray600}
                >
                  <img
                    className={'suggestion-img'}
                    src={SuggestionCardIconHover}
                    alt=""
                  />
                </Box>
                <CustomizedTypography variant="body1" color="textSecondary">
                  원하는 서비스가 없나요?
                </CustomizedTypography>
              </Box>
            ) : (
              <img src={img} alt="cnc" />
            )}

            <div className="servicetype-text">
              <CustomizedTypography variant="body1" fontWeight="bold">
                {servicetype}
              </CustomizedTypography>
            </div>
          </Box>
          {description && (
            <div className="servicetype-help" onClick={onClick}>
              <CustomizedTypography
                variant="body2"
                fontWeight="500"
                color="textSecondary"
              >
                {description}
              </CustomizedTypography>
              <ArrowForwardOutlined fontSize="small" />
            </div>
          )}
        </Grid>
      );
    }
  };

  return (
    <div className={classes.root}>
      <CustomizedTypography
        variant={matches ? 'h3' : 'h2'}
        fontWeight="bold"
        color="textPrimary"
        gutterBottoms={matches ? 12 : 8}
        align="center"
      >
        어떤 제조 서비스가 필요하세요?
      </CustomizedTypography>
      <div className="title-description" onClick={onClick}>
        <Help fontSize="small" />
        <CustomizedTypography
          variant="body2"
          fontWeight="500"
          color="textSecondary"
        >
          제조 서비스별 특징 한 눈에 보기
        </CustomizedTypography>
      </div>
      <Grid container spacing={2}>
        {TObject.map(({ description, img, servicetype }) => (
          <ServiceTypeCard
            key={servicetype}
            servicetype={servicetype}
            description={description}
            img={img}
            onClick={onClick}
          />
        ))}
        <ServiceTypeCard
          servicetype="서비스 개설 제안"
          description="원하는 서비스가 없으시다면?"
          img={SuggestionCardIcon}
          onClick={() => {
            window.open(
              'https://forms.office.com/Pages/ResponsePage.aspx?id=YgkiXAnoSU-_A-gopRvcT5MMzuCY1N5IislpZIGwDl1UM0hXSzE5VDlQR01WRUo3QzFGU1lYUkhQOS4u'
            );
          }}
        />
      </Grid>
    </div>
  );
};

export default ServiceTypeCardPresenter;

const TObject = [
  {
    servicetype: 'CNC 가공',
    description: 'CNC 가공이 궁금하다면?',
    img: CncImg
  },
  {
    servicetype: '3D 프린팅',
    description: '3D 프린팅이 궁금하다면?',
    img: PrintingImg
  },
  {
    servicetype: '금형사출',
    description: '금형사출이 궁금하다면?',
    img: MoldingImg
  },
  {
    servicetype: '판금 가공',
    description: '판금 가공이 궁금하다면?',
    img: SheetMetalImg
  },
  { servicetype: '주조', description: '주조가 궁금하다면?', img: CastingImg },
  {
    servicetype: '디자인/설계',
    description: '디자인/설계가 궁금하다면?',
    img: DesignImg
  },
  {
    servicetype: '전자회로(PCB)',
    description: '전자회로(PCB)가 궁금하다면?',
    img: ServiceType_Pcb_192
  }
];
